import { useState, type ReactElement } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useCompany } from './CompanyProvider';

/**
 * Schuetzt den App-Bereich gegen "kein Mandant": Wer noch keine Firma hat,
 * wird ins Onboarding geleitet. Laeuft INNERHALB von RequireAuth.
 *
 * WICHTIG: Ins Onboarding wird NUR geleitet, wenn die Firmenliste erfolgreich
 * geladen wurde und wirklich leer ist. Bei einem Ladefehler (401/429/5xx) sah
 * die App den Nutzer sonst faelschlich als "ohne Firma" und draengte ihn ins
 * Anlage-Formular — so entstanden versehentlich doppelte Mandanten.
 */
export default function RequireCompany(): ReactElement {
  const { companies, loading, error, reload } = useCompany();
  const [retrying, setRetrying] = useState(false);

  if (loading || retrying) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
      </div>
    );
  }

  // Ladefehler: nichts annehmen, sondern erneut versuchen lassen.
  if (error && companies.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-sm rounded-2xl border border-gray-200 p-6 text-center">
          <h1 className="text-lg font-semibold text-gray-900">Firmendaten nicht geladen</h1>
          <p className="mt-2 text-sm text-gray-600">{error}</p>
          <button
            type="button"
            onClick={() => {
              setRetrying(true);
              void reload().finally(() => setRetrying(false));
            }}
            className="mt-5 w-full rounded-xl bg-blue-600 px-4 py-2.5 font-medium text-white transition hover:bg-blue-700"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  if (companies.length === 0) {
    return <Navigate to="/onboarding" replace />;
  }

  return <Outlet />;
}
